import { useCanvasById } from "@/api/canvas";
import { useWorkspaces } from "@/api/workspaces";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import type { CanvasEntity, Entity } from "@cohesive/api-entities";
import {
  type CanvasLinkCardHydration,
  isCanvasLinkEntity,
} from "@cohesive/ui-components";
import { useMemo } from "react";

const LOOKUP_STALE_MS = 5 * 60 * 1000;

function isGoneStatus(status: number | undefined): boolean {
  return status === 403 || status === 404 || status === 410;
}

/**
 * Resolves a placed canvas link against the live canvas record: fresh name,
 * hero image and workspace location. The node keeps only id + name, so this
 * is what keeps a card current across renames and moves. No-ops for any
 * other entity, and on the public share view (no API auth).
 */
export function useHydrateCanvasLinkEntity(input: Entity | null | undefined): {
  entity: CanvasEntity | null;
  hydration: CanvasLinkCardHydration | undefined;
} {
  const isPublicCanvasView = useIsPublicCanvasView();
  const link = input && isCanvasLinkEntity(input) ? input : null;
  const canvasId = link?.data.id;
  const enabled = !!canvasId && !isPublicCanvasView;

  const { data: canvas, error } = useCanvasById(canvasId, {
    enabled,
    staleTime: LOOKUP_STALE_MS,
  });
  const { data: workspaces } = useWorkspaces({
    enabled: !!link && !isPublicCanvasView,
  });

  const isGone = isGoneStatus(error?.response?.status);
  const workspaceId = canvas?.workspace_id ?? link?.data.workspace_id;
  const workspace = workspaceId
    ? workspaces?.find((ws) => ws.id === workspaceId)
    : undefined;
  // Same rule as the browser's location column: a workspace outside the
  // user's own list is one the canvas was shared from.
  const locationLabel = !workspaceId
    ? undefined
    : workspace
      ? workspace.is_private
        ? "My workspace"
        : workspace.name
      : workspaces
        ? "Shared with me"
        : undefined;

  const entity = useMemo<CanvasEntity | null>(() => {
    if (!link) return null;
    if (!canvas) return link;
    return {
      ...link,
      data: {
        ...link.data,
        name: canvas.name,
        ...(canvas.workspace_id ? { workspace_id: canvas.workspace_id } : {}),
      },
    };
  }, [link, canvas]);

  const hydration = useMemo<CanvasLinkCardHydration | undefined>(() => {
    if (!link) return undefined;
    return {
      hero: canvas
        ? {
            hero_image_url: canvas.hero_image_url,
            hero_image_light_url: canvas.hero_image_light_url,
            hero_image_dark_url: canvas.hero_image_dark_url,
          }
        : undefined,
      locationLabel,
      isGone,
    };
  }, [link, canvas, locationLabel, isGone]);

  return { entity, hydration };
}
